import type { ComponentPropsWithRef } from 'react';

import { useTranslation } from 'react-i18next';

import { useIntl } from '~/hooks/useIntl';
import { clsx } from '~/styles/utils';

import { Button } from '../Button/Button';
import { Card } from '../Card/Card';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import { Icon } from '../Icon/Icon';
import { Progress } from '../Progress/Progress';
import { Text } from '../Text/Text';
import { usePreviewUrl } from './FileUpload.hooks';
import {
  actionsStyle,
  contentStyle,
  itemErrorStyle,
  itemStyle,
  metaStyle,
  nameStyle,
  progressStyle,
  thumbnailImageStyle,
  thumbnailStyle,
} from './UploadedFile.css';
import { formatFileSize } from './UploadedFile.utils';

type Props = ComponentPropsWithRef<typeof Card.Root> & {
  disabled?: boolean;
  error?: boolean;
  fileSize?: number;
  name?: string;
  onRemove: () => void;
  onRetry: () => void;
  /** Omit to hide the view action, e.g. while the file is not available yet. */
  onView?: () => void;
  preview?: Blob | string;
  /** Number is a fraction between 0 and 1, `null` is uploading by an unknown amount, `undefined` is idle. */
  progress?: number | null;
  /** Removal is in flight: the remove action shows a spinner and every action is inert. */
  removing?: boolean;
};

export const UploadedFile = ({
  disabled,
  error,
  fileSize,
  name,
  onRemove,
  onRetry,
  onView,
  preview,
  progress,
  removing,
  ...delegated
}: Props) => {
  const { locale } = useIntl();

  const { t } = useTranslation(['common']);

  const previewUrl = usePreviewUrl(preview);

  const isDisabled = disabled || removing;

  return (
    <Card.Root {...delegated} className={clsx(itemStyle, error && itemErrorStyle, delegated.className)} size={delegated.size ?? 'small'}>
      <div className={thumbnailStyle}>
        {previewUrl === undefined ? <Icon name="File" size={24} /> : <img alt="" className={thumbnailImageStyle} src={previewUrl} />}
      </div>

      <div className={contentStyle}>
        <Text className={nameStyle}>{name ?? t('common:fileUpload.unnamedFile')}</Text>

        {fileSize !== undefined && <Text className={metaStyle}>{formatFileSize(fileSize, locale)}</Text>}

        {error && <ErrorMessage>{t('common:fileUpload.error')}</ErrorMessage>}

        {progress !== undefined && (
          <Progress aria-label={t('common:fileUpload.uploading')} className={progressStyle} type="percent" value={progress} />
        )}
      </div>

      <div className={actionsStyle}>
        {error && <Button aria-label={t('common:fileUpload.retry')} disabled={isDisabled} icon="Refresh" onClick={onRetry} />}

        {onView && <Button aria-label={t('common:fileUpload.view')} disabled={isDisabled} icon="Visibility" onClick={onView} />}

        <Button aria-label={t('common:fileUpload.remove')} disabled={disabled} icon="CloseSmall" loading={removing} onClick={onRemove} />
      </div>
    </Card.Root>
  );
};
